import { FONT_SIZE, LABEL_CELL_SX } from "./constants";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";

export const SectionHeader = ({
  label,
  open,
  onToggle,
  action,
  colSpan = 2,
}) => (
  <TableRow
    sx={{ backgroundColor: "grey.100", cursor: "pointer" }}
    onClick={onToggle}
  >
    <TableCell colSpan={colSpan}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography
          fontSize={"0.7rem"}
          fontWeight={700}
          color={"text.secondary"}
        >
          {label}
        </Typography>
        <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
          {action && <Box onClick={(e) => e.stopPropagation()}>{action}</Box>}
          {open ? (
            <ExpandLessIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          )}
        </Stack>
      </Stack>
    </TableCell>
  </TableRow>
);

export const CollapsibleRows = ({ open, children, colSpan = 2 }) => (
  <TableRow sx={{ p: 0 }}>
    <TableCell colSpan={colSpan} sx={{ p: 0, border: 0 }}>
      <Collapse in={open} timeout={"auto"} unmountOnExit>
        <Table size={"small"} sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableBody>{children}</TableBody>
        </Table>
      </Collapse>
    </TableCell>
  </TableRow>
);

export const Row = ({ label, value, values, colSpan, bold }) => (
  <TableRow>
    <TableCell sx={LABEL_CELL_SX}>
      <Typography fontSize={FONT_SIZE} fontWeight={bold ? 700 : 400}>
        {label}
      </Typography>
    </TableCell>
    {values ? (
      values.map((v, i) => (
        <TableCell key={i} align={"right"}>
          <Tooltip title={String(v ?? "")}>
            <Typography fontSize={FONT_SIZE} fontWeight={bold ? 700 : 400}>
              {v ?? "—"}
            </Typography>
          </Tooltip>
        </TableCell>
      ))
    ) : (
      <TableCell colSpan={colSpan} align={"right"}>
        <Tooltip title={String(value ?? "")}>
          <Typography fontSize={FONT_SIZE} fontWeight={bold ? 700 : 400}>
            {value ?? "—"}
          </Typography>
        </Tooltip>
      </TableCell>
    )}
  </TableRow>
);

export const SelectRow = ({
  flag,
  value,
  onChange,
  disabled,
  isInvalid,
  isLegacy,
  valueColSpan = 1,
}) => {
  const hasWarning = isInvalid || isLegacy;
  const warningTitle = isLegacy
    ? "This factor was used in the last pricing run but has been removed from the current default configuration."
    : "Previous selection no longer exists in the current configuration. Please choose a new value";
  return (
    <TableRow sx={isInvalid ? { bgcolor: "warning.light" } : undefined}>
      <TableCell sx={LABEL_CELL_SX}>
        <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
          {hasWarning && (
            <Tooltip title={warningTitle}>
              <WarningIcon sx={{ fontSize: 13, color: "warning.dark" }} />
            </Tooltip>
          )}
          <Typography
            fontSize={FONT_SIZE}
            color={hasWarning ? "warning.dark" : "inherit"}
            fontWeight={hasWarning ? 600 : 400}
          >
            {flag.displayName ?? flag.type}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell colSpan={valueColSpan} align={"right"}>
        <Select
          size={"small"}
          value={value ?? ""}
          onChange={(e) => onChange(flag.type, e.target.value)}
          disabled={disabled}
          error={hasWarning}
          sx={{ fontSize: FONT_SIZE, minWidth: 180 }}
        >
          {(flag.categoryOptions ?? []).map(({ key, display }) => (
            <MenuItem key={key} value={key}>
              {display}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
    </TableRow>
  );
};

export const InputRow = ({
  flag,
  value,
  onChange,
  disabled,
  valueColSpan = 1,
}) => {
  const hasError = value !== "" && value != null && Number(value) <= 0;
  return (
    <TableRow>
      <TableCell sx={LABEL_CELL_SX}>
        <Typography fontSize={FONT_SIZE} color={hasError ? "error" : "inherit"}>
          {flag.displayName ?? flag.type}
        </Typography>
      </TableCell>
      <TableCell colSpan={valueColSpan} align={"right"}>
        <Tooltip
          title={hasError ? "Value must be > 0" : ""}
          open={hasError}
          placement={"left"}
        >
          <PrettyNumberTextField
            size={"small"}
            hiddenLabel
            value={value}
            onChangeNumber={(nextValue) =>
              onChange(flag.type, nextValue == null ? "" : nextValue)
            }
            disabled={disabled}
            allowClear
            variant={"filled"}
            error={hasError}
            inputProps={{ style: { textAlign: "right" } }}
            sx={{ width: 180 }}
            fullWidth={false}
          />
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};
